<template>
  <v-container :fluid="$vuetify.display.mdAndDown">
    <alert-card
        v-if="alert.visible === true"
        v-model:visible="alert.visible"
        :type="alert.type"
        :title="alert.title"
        :text="alert.text"
        :closable="false"
    />
    <v-layout
        justify-center
        align-center
    >
      <v-col cols="12">
        <car-card-skeleton v-if="loading || alert.visible"/>
        <car-card
            v-else
            :title="car.manufacturer + ', ' + car.model"
            :photos="car.photos"
            :year="car.year"
            :engine="car.engine"
            :fuel="car.fuel"
            :transmission="car.transmission"
            :color="car.color"
            :mileage="car.mileage"
            :specification="car.specification"
            :first-price="numbers.formatMoney(car.prices.filter((currency) => currency.currency === Currency.KRW)[0].value, Currency.KRW)"
            :second-price="numbers.formatMoney(car.prices.filter((currency) => currency.currency === Currency.USD)[0].value, Currency.USD)"
            :third-price="numbers.formatMoney(car.prices.filter((currency) => currency.currency === Currency.EUR)[0].value, Currency.EUR)"
            :is-authorized="isAuthorized"
            :contact-us="contactUs"
            :is-checkup-visible="isCheckupVisible"
            :check-up="checkUp"
            :sendRequest="sendRequest"
            :car-ad-list="carAds"
            :equipment-groups="specificationDetails"
            :description="description"
            v-model:isRequested="car.isRequested"
            @load-car-ads="getCarAds"
        />
      </v-col>
    </v-layout>
  </v-container>
</template>

<script>
import CarCard from "@/components/Car/CarCard.vue";
import {auth} from "@/common/auth";
import {api} from "@/common/api";
import {router} from "@/router";
import {inject} from "vue";
import CarCardSkeleton from "@/components/Car/CarCardSkeleton.vue";
import AlertCard from "@/components/AlertCard.vue";
import {messages} from "@/common/enum/messages";
import {Currency} from "@/common/enum/currency";
import {numbers} from "@/common/numbers";
import {CONFIG} from "@/common/config";

export default {
  name: "CarView",
  components: {AlertCard, CarCardSkeleton, CarCard},
  emits: ['set-livechat-session-data'],
  props: {
    id: String
  },
  computed: {
    numbers() {
      return numbers
    },
    Currency() {
      return Currency
    }
  },
  data() {
    return {
      isAuthorized: false,
      isCheckupVisible: false,
      car: {},

      carAdsListCallback: null,
      carAdsPage: 1,
      carAdsLimit: 5,
      carAdsTotal: null,
      carAds: [],

      specificationDetails: {},
      description: null,
      loading: true,
      alert: {
        visible: false,
        type: 'error',
        title: 'Error',
        text: ''
      }
    }
  },
  mounted() {
    this.getCar()
    this.isAuthorized = auth.isAuthorized()
    if (this.isAuthorized !== false) {
      this.loadAccountStatistics()
    }
  },
  setup() {
    return {
      switchAuthorizationState: inject('authorization').switchState
    }
  },
  methods: {
    loadAccountStatistics() {
      const token = auth.getToken()
      if (token == null) {
        this.switchAuthorizationState()
        router.push({name: 'sign-in', state: {redirectTo: 'home'}})
        return
      }

      api.getAccountStatistics(token, this.setAccountStatistics)
    },
    setAccountStatistics(response) {
      if (response?.data?.status === 'error') {
        return
      }
      const balance = response.data.balances.filter((balance) => balance.currency === Currency.USD)[0].balance_amount ?? 0
      this.isCheckupVisible = balance > 1000
    },
    getCar() {
      this.loading = true
      const token = auth.getToken()
      api.getCarById(token, this.id, this.setCar)
    },
    setCar(response) {
      if (response?.data?.status === 'error') {
        this.alert.visible = true
        this.alert.type = 'error'
        this.alert.title = 'Error'
        this.alert.text = response.data?.message?.length === 0 ? messages.en.car_view.car_loading_error : response.data.message
        return
      }

      this.loading = false
      this.alert.visible = false
      let car = response.data
      this.car.id = car.id
      this.car.manufacturer = car.manufacturer.english_name
      this.car.model = car.model.english_name
      this.car.photos = car.car_photos.map(photo => {
        return CONFIG.API_HOST + '/api/v1/car-photos/' + photo.id
      });
      this.car.year = car.year.value
      this.car.engine = car.engine
      this.car.fuel = car.fuel_type.english_name
      this.car.transmission = car.transmission.english_name
      this.car.color = car.color
      this.car.mileage = car.mileage.value
      this.car.specification = car.specification.english_name

      this.car.prices = car.public_prices
      if (car.car_request && car.car_request.id) {
        this.car.isRequested = true
      }

      for (const index in car.equipments) {
        const equipment = car.equipments[index]
        const mappedEquipment = {
          englishName: equipment.english_name,
          isExists: equipment.is_exist
        }
        if (this.specificationDetails[equipment.equipment_group.english_name]) {
          this.specificationDetails[equipment.equipment_group.english_name].equipments.push(mappedEquipment)
          continue
        }

        this.specificationDetails[equipment.equipment_group.english_name] = {
          englishName: equipment.equipment_group.english_name,
          equipments: [mappedEquipment]
        }
      }

      this.description = car.description
    },
    getCarAds(event) {
      this.carAdsListCallback = event.done

      if (this.carAdsTotal !== null && this.carAdsTotal - this.carAdsLimit * (this.carAdsPage - 1) <= 0) {
        this.carAdsListCallback('empty')
        return
      }

      const token = auth.getToken()
      api.getCars(token, {
        page: this.carAdsPage,
        limit: this.carAdsLimit,
        similar_car_id: this.id
      }, null, this.setCarAds)
    },
    setCarAds(response) {
      if (response?.data?.status === 'error') {
        this.carAdsListCallback('error')
        return
      }

      const cars = response.data.cars
      this.carAdsTotal = response.data.total
      this.carAds = this.carAds.concat(
          cars.map(car => {
            return {
              id: car.id,
              title: car.manufacturer.english_name + ' ' + car.model.english_name + ', ' + car.year.value,
              prices: car.public_prices,
              subtitle: car.specification.english_name + ', ' + car.engine + 'cc, ' + numbers.addGapsBetweenPlaces(car.mileage.value) + ' km',
              description: car.description,
              mainPhoto: CONFIG.API_HOST + '/api/v1/car-photos/' + car.main_photo.id
            }
          })
      )

      if (this.carAdsTotal - this.carAdsLimit * (this.carAdsPage - 1) <= 0) {
        this.carAdsListCallback('empty')
      } else {
        this.carAdsListCallback('ok')
      }
      this.carAdsPage++
    },
    async sendRequest() {
      const token = auth.getToken()
      if (token == null) {
        this.switchAuthorizationState()
        await router.push({name: 'sign-in', state: {redirectTo: 'home'}})
        return
      }

      await api.sendCarRequest(token, {
        car_id: this.car.id
      })
      this.car.isRequested = true
    },
    contactUs() {
      this.$emit('set-livechat-session-data',
          {
            id: this.car.id,
            title: this.car.manufacturer + ', ' + this.car.model + ', ' + this.car.year,
            price: this.car.prices.filter((currency) => currency.currency === Currency.KRW)[0].value + ' ₩',
            action: 'Client Contact Us action'
          }
      )
    },
    checkUp() {
      this.$emit('set-livechat-session-data',
          {
            id: this.car.id,
            title: this.car.manufacturer + ', ' + this.car.model + ', ' + this.car.year,
            price: this.car.prices.filter((currency) => currency.currency === Currency.KRW)[0].value + ' ₩',
            action: 'Client Check Up action'
          }
      )
    }
  },
}
</script>

<style scoped>

</style>